import { Fragment, useState } from "react";
import { v1 as uuidv1 } from "uuid";

import { mergeClassNames } from "@bokio/utils/classes";

import { Validation } from "../Form";

import type { FieldValidationResult } from "@bokio/shared/validation/entityValidator";
import type { ChangeEvent, ReactNode } from "react";

import * as styles from "./checkBox.scss";

export interface CheckBoxProps {
	label?: ReactNode;
	id?: string;
	checked?: boolean;
	disabled?: boolean;
	onChange?: (checked: boolean) => void;
	/**
	 * @deprecated Should only be used for design system internal overrides.
	 */
	className?: string;
	rightMargin?: boolean; // This should be controlled by the parent not here
	noRightMargin?: boolean; // This should not be needed because there should not be any default margin on the component.
	errors?: FieldValidationResult;
	testId?: string;
	/**
	 * Partial should be used in cases where we have a group checkbox and some of the children are checked.
	 * "checked" must be true for this to work
	 */
	partial?: boolean;
}

export const CheckBox = (props: CheckBoxProps) => {
	const { label, errors, className, disabled } = props;

	const [uniqueId] = useState(() => props.id || uuidv1());

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (props.onChange) {
			props.onChange(!props.checked);
		}
		e.stopPropagation();
	};

	const renderInput = (className?: string) => {
		const { rightMargin, testId, checked, partial, disabled, id } = props;

		return (
			<input
				data-testid={testId}
				disabled={disabled}
				type="checkbox"
				onChange={onChange}
				className={mergeClassNames(
					styles.checkBox,
					className,
					rightMargin && styles.rightMargin,
					checked && partial && styles.partial,
					props.noRightMargin && styles.noRightMargin,
				)}
				checked={!!checked}
				onClick={e => e.stopPropagation()}
				id={id ?? uniqueId}
			/>
		);
	};

	return (
		<Fragment>
			{label ? (
				<label
					htmlFor={uniqueId}
					className={mergeClassNames(styles.label, disabled && styles.labelDisabled, className)}
				>
					{renderInput()}
					{label}
					<Validation errors={errors && errors.errors} />
				</label>
			) : (
				renderInput(className)
			)}
		</Fragment>
	);
};
